import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  CSSProperties,
  SVGAttributes,
  useRef,
} from 'react'
import { NodeGroup } from 'react-move'
import { easeCubic } from 'd3-ease'
import { scaleSqrt } from 'd3-scale'
import { extent } from 'd3-array'
import { interpolate } from 'd3-interpolate'
import { useAtom } from 'jotai'
import {
  useGG,
  focusNodes,
  unfocusNodes,
  EventArea,
  themeState,
  radiusScaleState,
  zoomState,
  xScaleState,
  yScaleState,
  isDate,
  defineGroupAccessor,
  Aes,
  BrushAction,
  usePageVisibility,
} from '@graphique/graphique'
import { Entrance, type GeomAes } from './types'
import { Tooltip } from './tooltip'

export interface GeomProps<Datum> {
  /**
   * **data used by this Geom**
   *
   * This will overwrite top-level `data` passed to `GG` as it relates to mappings defined in `aes`.
   */
  data?: Datum[]
  /**
   * **functional mapping applied to `data` for this Geom**
   *
   * This extends the top-level `aes` passed to `GG`. Any repeated mappings defined here will take precedence within the Geom.
   */
  aes?: GeomAes<Datum>
  /** attributes passed to the underlying SVG elements */
  attr?: SVGAttributes<SVGCircleElement>
  /** should this Geom have a tooltip associated with it (_default_: `true`) */
  showTooltip?: boolean
  /** determines what happens when brushing (clicking and dragging) over the drawing area  */
  brushAction?: BrushAction
  /** where elements should start as they enter the drawing area (_default_: `Entrance.BOTTOM`) */
  entrance?: Entrance
  /** should elements be strictly clipped at the bounds of the drawing area (_default_: `false`) */
  isClipped?: boolean
  /** array of keys (of the kind that are generated by `aes.key`) used to programmatically focus associated points */
  focusedKeys?: string[]
  /** styles applied to focused elements */
  focusedStyle?: CSSProperties
  /** styles applied to unfocused elements */
  unfocusedStyle?: CSSProperties
  /** callback called for mousemove events on the drawing area when focusing data */
  onDatumFocus?: (data: Datum[], index: number[]) => void
  /** callback called for click events on the drawing area when selecting focused data */
  onDatumSelection?: (data: Datum[], index: number[]) => void
  /** callback called for mouseleave events on the drawing area */
  onExit?: () => void
  /** should elements enter/update/exit with animated transitions (_default_: `true`) */
  isAnimated?: boolean
}

const GeomPoint = <Datum,>({
  data: localData,
  aes: localAes,
  focusedStyle,
  unfocusedStyle,
  attr,
  focusedKeys = [],
  onDatumFocus,
  onDatumSelection,
  entrance = Entrance.BOTTOM,
  onExit,
  showTooltip = true,
  brushAction,
  isClipped = false,
  isAnimated = true,
}: GeomProps<Datum>) => {
  const { ggState } = useGG<Datum>() || {}
  const { id, data, aes, scales, copiedScales, height, margin } = ggState || {}

  const [theme, setTheme] = useAtom(themeState)
  const [radiusScale] = useAtom(radiusScaleState)
  const [{ xDomain: xZoomDomain, yDomain: yZoomDomain }] = useAtom(zoomState)
  const [{ isFixed: isFixedX }] = useAtom(xScaleState)
  const [{ isFixed: isFixedY }] = useAtom(yScaleState)

  const isVisible = usePageVisibility()

  const baseAttr: SVGAttributes<SVGCircleElement> = {
    r: 3.5,
    fillOpacity: 1,
    strokeOpacity: 1,
  }

  const geomAttr: SVGAttributes<SVGCircleElement> = {
    ...baseAttr,
    ...attr,
  }

  const { domain: sizeDomain, range: sizeRange } = radiusScale || {}
  const { defaultFill, animationDuration: duration } = theme

  const initialGeomData = useMemo(() => localData || data, [data, localData])

  const geomAes = useMemo(() => {
    if (localAes) {
      return {
        ...aes,
        ...localAes,
      }
    }
    return aes
  }, [aes, localAes])

  const group = useMemo(
    () => geomAes && defineGroupAccessor(geomAes as Aes<Datum>),
    [geomAes, defineGroupAccessor],
  )

  const positionKeyAccessor = useCallback(
    (d: Datum) =>
      `${geomAes?.x && geomAes.x(d)}-${geomAes?.y && geomAes.y(d)}-${
        group && group(d)
      }` as string,
    [geomAes, group],
  )

  const keyAccessor = useCallback(
    (d: Datum) => (geomAes?.key ? geomAes.key(d) : positionKeyAccessor(d)),
    [geomAes, group, positionKeyAccessor],
  )

  const undefinedX = useMemo(
    () =>
      initialGeomData
        ? initialGeomData.filter(
            (d) =>
              geomAes?.x &&
              (geomAes.x(d) === null ||
                typeof geomAes.x(d) === 'undefined' ||
                (isDate(geomAes.x(d)) &&
                  Number.isNaN(geomAes.x(d)?.valueOf()))),
          )
        : [],
    [initialGeomData, geomAes],
  )
  const undefinedY = useMemo(
    () =>
      initialGeomData
        ? initialGeomData.filter(
            (d) =>
              geomAes?.y &&
              (geomAes.y(d) === null || typeof geomAes.y(d) === 'undefined'),
          )
        : [],
    [initialGeomData],
  )

  const validFocusedKeys = useMemo(
    () => focusedKeys.filter((v) => v),
    [focusedKeys],
  )

  const geomData = useMemo(() => {
    const presentData = initialGeomData?.filter(
      (d) =>
        geomAes?.x &&
        geomAes?.x(d) !== null &&
        !(typeof geomAes?.x(d) === 'undefined') &&
        (isDate(geomAes?.x(d))
          ? !Number.isNaN(geomAes?.x(d)?.valueOf())
          : true) &&
        geomAes.y &&
        geomAes.y(d) !== null &&
        !(typeof geomAes.y(d) === 'undefined'),
    )

    const uniqueKeyVals = Array.from(
      new Set(presentData?.map((d) => keyAccessor(d))),
    )

    return uniqueKeyVals.flatMap((k) => {
      const dataWithKey = presentData?.filter((d) => keyAccessor(d) === k)
      if (dataWithKey && dataWithKey.length > 1) {
        return dataWithKey.map((dk: any, i) => ({
          ...dk,
          gg_gen_index: i,
        }))
      }
      return dataWithKey?.flat()
    })
  }, [initialGeomData, keyAccessor])

  const positionKeys = useMemo(
    () => geomData.map(positionKeyAccessor).join(''),
    [geomData, positionKeyAccessor],
  )

  const [firstRender, setFirstRender] = useState(true)
  useEffect(() => {
    const timeout = setTimeout(() => setFirstRender(false), 0)
    return () => clearTimeout(timeout)
  }, [])

  useEffect(() => {
    if (firstRender && undefinedX.length > 0) {
      console.warn(
        `Ignoring ${undefinedX.length} points with missing x values.`,
      )
    }

    if (firstRender && undefinedY.length > 0) {
      console.warn(
        `Ignoring ${undefinedY.length} points with missing y values.`,
      )
    }
  }, [firstRender, undefinedX, undefinedY])

  const bottomPos = useMemo(
    () => (height && margin ? height - margin.bottom : undefined),
    [height, margin],
  )

  useEffect(() => {
    setTheme((prev) => ({
      ...prev,
      geoms: {
        ...prev.geoms,
        point: {
          fillOpacity: geomAttr.style?.fillOpacity || geomAttr.fillOpacity,
          stroke: geomAttr.stroke,
          strokeWidth: geomAttr.style?.strokeWidth || geomAttr.strokeWidth,
          strokeOpacity:
            geomAttr.style?.strokeOpacity || geomAttr.strokeOpacity,
          size: geomAes?.size,
        },
      },
    }))
  }, [attr, setTheme])

  const baseStyles: CSSProperties = {
    transition: 'fill-opacity 200ms',
    fillOpacity: geomAttr.fillOpacity,
    strokeOpacity: geomAttr.strokeOpacity,
    ...geomAttr.style,
  }

  const focusedStyles: CSSProperties = {
    ...baseStyles,
    ...focusedStyle,
  }

  const unfocusedStyles: CSSProperties = {
    ...baseStyles,
    fillOpacity: 0.2,
    strokeOpacity: 0.2,
    ...unfocusedStyle,
  }

  const fill = useMemo(
    () => (d: Datum) =>
      geomAttr.fill ||
      (geomAes?.fill && copiedScales?.fillScale
        ? (copiedScales.fillScale(geomAes.fill(d)) as string | undefined)
        : defaultFill),
    [geomAes, copiedScales, geomAttr, defaultFill],
  )

  const stroke = useMemo(
    () => (d: Datum) =>
      geomAttr.stroke ||
      (geomAes?.stroke && copiedScales?.strokeScale
        ? (copiedScales.strokeScale(geomAes.stroke(d) as any) as
            | string
            | undefined)
        : 'none'),
    [geomAes, copiedScales, geomAttr],
  )

  const radius = useMemo(() => {
    if (geomData && geomAes?.size && sizeRange) {
      const domain =
        sizeDomain && sizeDomain[0] && sizeDomain[1]
          ? sizeDomain
          : (extent(geomData, geomAes.size as () => number) as number[])
      return scaleSqrt()
        .domain(domain)
        .range(sizeRange as [number, number])
        .unknown([geomAttr.r])
    }
    return () => geomAttr.r
  }, [geomAttr, geomAes, geomData, sizeRange, sizeDomain])

  const x = useMemo(() => {
    if (scales?.xScale.bandwidth) {
      return (d: Datum) =>
        (scales?.xScale(geomAes?.x && geomAes.x(d)) || 0) +
        scales?.xScale.bandwidth() / 2 +
        0.9
    }
    return (d: Datum) =>
      scales?.xScale && geomAes?.x && (scales.xScale(geomAes.x(d)) || 0)
  }, [scales, geomAes])

  const y = useMemo(() => {
    if (scales?.yScale.bandwidth) {
      return (d: Datum) =>
        (scales?.yScale(geomAes?.y && geomAes.y(d)) || 0) +
        scales?.yScale.bandwidth() / 2
    }
    return (d: Datum) =>
      scales?.yScale && geomAes?.y && (scales.yScale(geomAes.y(d)) || 0)
  }, [scales, geomAes])

  const groupRef = useRef<SVGGElement>(null)
  const points = groupRef.current?.getElementsByTagName('circle')

  const [shouldClip, setShouldClip] = useState(
    isClipped || isFixedX || isFixedY,
  )
  useEffect(() => {
    if (xZoomDomain?.current || yZoomDomain?.current) {
      setShouldClip(true)
    } else {
      const timeout = setTimeout(() => setShouldClip(isClipped), duration)
      return () => clearTimeout(timeout)
    }
    return undefined
  }, [isFixedX, isFixedY, xZoomDomain?.current, yZoomDomain?.current, duration])

  return (
    <>
      <g
        ref={groupRef}
        clipPath={shouldClip ? `url(#__gg_canvas_${id})` : undefined}
      >
        {!firstRender && isVisible && (
          <NodeGroup
            data={[...(geomData as any[])]}
            keyAccessor={(d) =>
              geomAes?.key
                ? keyAccessor(d)
                : `${keyAccessor(d)}-${d.gg_gen_index}`
            }
            start={(d) => ({
              cx: x(d),
              cy: entrance === Entrance.DATA ? y(d) : bottomPos,
              fill: fill(d),
              stroke: stroke(d),
              r: 0,
              fillOpacity: 0,
              strokeOpacity: 0,
            })}
            enter={(d) => ({
              cx: isAnimated ? [x(d)] : x(d),
              cy: isAnimated ? [y(d)] : y(d),
              r: isAnimated
                ? [
                    attr?.r ??
                      (geomAes?.size
                        ? radius(geomAes.size(d) as number)
                        : radius(d)),
                  ]
                : attr?.r ??
                  (geomAes?.size
                    ? radius(geomAes.size(d) as number)
                    : radius(d)),
              fill: isAnimated ? [fill(d)] : fill(d),
              stroke: isAnimated ? [stroke(d)] : stroke(d),
              fillOpacity: isAnimated
                ? [geomAttr.fillOpacity]
                : geomAttr.fillOpacity,
              strokeOpacity: isAnimated
                ? [geomAttr.strokeOpacity]
                : geomAttr.strokeOpacity,
              timing: { duration, ease: easeCubic },
            })}
            update={(d) => ({
              cx: isAnimated ? [x(d)] : x(d),
              cy: isAnimated ? [y(d)] : y(d),
              r: isAnimated
                ? [
                    attr?.r ??
                      (geomAes?.size
                        ? radius(geomAes.size(d) as number)
                        : radius(d)),
                  ]
                : attr?.r ??
                  (geomAes?.size
                    ? radius(geomAes.size(d) as number)
                    : radius(d)),
              fill: isAnimated ? [fill(d)] : fill(d),
              stroke: isAnimated ? [stroke(d)] : stroke(d),
              fillOpacity: isAnimated
                ? [geomAttr.fillOpacity]
                : geomAttr.fillOpacity,
              strokeOpacity: isAnimated
                ? [geomAttr.strokeOpacity]
                : geomAttr.strokeOpacity,
              timing: { duration, ease: easeCubic },
            })}
            leave={() => ({
              fill: isAnimated ? ['transparent'] : 'transparent',
              stroke: isAnimated ? ['transparent'] : 'transparent',
              cy: isAnimated ? [bottomPos] : bottomPos,
              timing: { duration, ease: easeCubic },
            })}
            interpolation={(begVal, endVal) => interpolate(begVal, endVal)}
          >
            {(nodes) => (
              <>
                {nodes.map(({ state, key }) => {
                  let styles = {}
                  if (validFocusedKeys.includes(key)) styles = focusedStyles
                  if (
                    validFocusedKeys?.length > 0 &&
                    !validFocusedKeys.includes(key)
                  )
                    styles = unfocusedStyles
                  return (
                    <circle
                      key={key}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...attr}
                      r={state.r >= 0 ? state.r : geomAttr.r}
                      fill={state.fill}
                      stroke={state.stroke}
                      cx={state.cx}
                      cy={state.cy}
                      fillOpacity={state.fillOpacity}
                      strokeOpacity={state.strokeOpacity}
                      style={{
                        pointerEvents: 'none',
                        ...baseStyles,
                        ...styles,
                      }}
                      data-testid="__gg_geom_point"
                    />
                  )
                })}
              </>
            )}
          </NodeGroup>
        )}
      </g>
      {(showTooltip || brushAction) && geomAes && (
        <>
          <EventArea
            data={geomData}
            showTooltip={showTooltip}
            brushAction={brushAction}
            aes={geomAes}
            x={x}
            y={y}
            onDatumFocus={onDatumFocus}
            onMouseOver={({ i }: { d: unknown; i: number[] }) => {
              const focusedIndexes = geomData.flatMap((gd, fi) =>
                validFocusedKeys.includes(keyAccessor(gd)) ? fi : [],
              )

              if (points) {
                focusNodes({
                  nodes: points,
                  focusedIndex: [...focusedIndexes, ...[i].flat()],
                  focusedStyles,
                  unfocusedStyles,
                })
              }
            }}
            onClick={
              onDatumSelection
                ? ({ d, i }: { d: any; i: number[] }) => {
                    onDatumSelection(d, i)
                  }
                : undefined
            }
            onMouseLeave={() => {
              if (points) {
                unfocusNodes({ nodes: points, baseStyles })
                if (validFocusedKeys && validFocusedKeys.length) {
                  focusNodes({
                    nodes: points,
                    focusedIndex: geomData.flatMap((d, i) =>
                      validFocusedKeys.includes(keyAccessor(d)) ? i : [],
                    ),
                    focusedStyles,
                    unfocusedStyles,
                  })
                }
              }

              if (onExit) onExit()
            }}
            positionKeys={positionKeys}
          />
          {showTooltip && <Tooltip aes={geomAes} group={group} />}
        </>
      )}
    </>
  )
}

GeomPoint.displayName = 'GeomPoint'
export { GeomPoint }
